import { VIEW_MODES, CLUSTER_STROKE_WIDTH } from '@aytm/app/stats/page/question-views/hm/components/chart/lib/constants'
import { getImageAssetUrl } from '@aytm/app/stats/page/question-views/hm/components/chart/lib/utils'

// Resolves to null for an artwork that cannot be read -- an expired asset answers 403 with an
// XML body, which rejects the fetch and throws out of json(). Callers treat "no artwork" as a
// state; a rejection here would only surface as an unhandled console error. The one rethrow is
// the caller's own AbortError: teardown must stay distinguishable from a missing artwork.
export const loadImageAsBase64 = async (imagePath, { signal } = {}) => {
  try {
    const urlResponse = await fetch(getImageAssetUrl(imagePath, { json: true }), { signal })
    if (!urlResponse.ok) return null

    const urlData = await urlResponse.json()
    if (!urlData?.url) return null

    const imageResponse = await fetch(urlData.url, { signal })
    if (!imageResponse.ok) return null

    const blob = await imageResponse.blob()

    return await new Promise(resolve => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    if ('AbortError' === error?.name) throw error
    return null
  }
}

// The clusters are drawn under the view they overlay; both are addressed by the id each was given.
const CLUSTERS_SVG = 'svg[id^="svg-clusters-"]'
const VIEW_SVG = 'svg:not([id^="svg-clusters-"])'

const appendClustersToSvg = (svgNode, container) => {
  const clustersSvg = container.querySelector(CLUSTERS_SVG)
  if (!clustersSvg) return

  Array.from(clustersSvg.children).forEach(child => {
    if (child.tagName !== 'defs') {
      const clonedChild = child.cloneNode(true)
      if (clonedChild.tagName === 'path') {
        clonedChild.style.strokeWidth = `${CLUSTER_STROKE_WIDTH}px`
      }
      svgNode.appendChild(clonedChild)
    }
  })
}

export const buildSvgForExport = (container, { id, viewMode, imgBase64, imageWidth, imageHeight, showClusters }) => {
  if (!container || !imgBase64) return null

  let svgNode

  if (viewMode === VIEW_MODES.ORIGINAL) {
    svgNode = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svgNode.setAttribute('version', '1.1')
    svgNode.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    svgNode.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink')
    svgNode.setAttribute('width', imageWidth)
    svgNode.setAttribute('height', imageHeight)
    svgNode.setAttribute('viewBox', `0 0 ${imageWidth} ${imageHeight}`)
  } else {
    const viewSvg = container.querySelector(VIEW_SVG)
    if (!viewSvg) return null

    svgNode = viewSvg.cloneNode(true)
    svgNode.style.transform = 'none'
    svgNode.style.position = 'relative'

    if (viewMode === VIEW_MODES.SPOTLIGHT) {
      const imageEl = svgNode.querySelector(`#image-source-${id}`)
      if (imageEl) {
        imageEl.setAttribute('href', imgBase64)
        imageEl.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', imgBase64)
      }
    } else if (viewMode === VIEW_MODES.OVERLAY || viewMode === VIEW_MODES.OVERLAYCLUSTERS) {
      const feImageEl = svgNode.querySelector(`#filter-${viewMode}-image-${id}`)
      if (feImageEl) {
        feImageEl.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', imgBase64)
        feImageEl.setAttribute('href', imgBase64)
      }
    } else {
      const imageNode = document.createElementNS('http://www.w3.org/2000/svg', 'image')
      imageNode.setAttribute('height', `${imageHeight}px`)
      imageNode.setAttribute('width', `${imageWidth}px`)
      imageNode.setAttribute('x', '0')
      imageNode.setAttribute('y', '0')
      imageNode.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', imgBase64)
      imageNode.setAttribute('href', imgBase64)

      svgNode.insertBefore(imageNode, svgNode.firstChild)
    }
  }

  if (showClusters) appendClustersToSvg(svgNode, container)

  return svgNode
}

export const buildSvgForPopup = (container, viewMode, showClusters) => {
  if (!container) return null

  const isOriginal = viewMode === VIEW_MODES.ORIGINAL
  const viewSvg = isOriginal ? null : container.querySelector(VIEW_SVG)
  const svgNode = viewSvg?.cloneNode(true) ?? document.createElementNS('http://www.w3.org/2000/svg', 'div')

  if (showClusters) appendClustersToSvg(svgNode, container)

  svgNode.querySelectorAll('defs').forEach(def => def.remove())

  return svgNode
}
